import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

type PageState = 'idle' | 'recording' | 'recorded' | 'running' | 'done' | 'failed';

interface BenchmarkRecord {
  id: number;
  testName?: string;
  audioFilename?: string;
  audioDurationS?: number;
  audioSizeBytes?: number;
  modelUsed?: string;
  language?: string;
  transcription?: string;
  modelLoadMs?: number;
  transcribeMs?: number;
  alignMs?: number;
  totalMs?: number;
  wordCount?: number;
  segmentCount?: number;
  status?: string;
  errorMessage?: string;
  createdAt?: string;
  segments?: any[];
  extractedName?: string;
  extractedMobile?: string;
  extractedCnic?: string;
  _expanded?: boolean;
}

interface HistoryPage {
  records: BenchmarkRecord[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

interface WorkerHealth {
  status?: string;
  model?: string;
  model_load_ms?: number;
  error?: string;
}

@Component({
  selector: 'app-rnd-whisper-benchmark',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './rnd-whisper-benchmark.component.html',
  styleUrls: ['./rnd-whisper-benchmark.component.scss'],
})
export class RndWhisperBenchmarkComponent implements OnInit, OnDestroy {
  state: PageState = 'idle';
  elapsedSeconds = 0;
  audioUrl = '';
  uploadedAudioUrl = '';
  mimeType = '';
  fileSize = 0;

  testName = '';
  language = 'ur';
  selectedFile: File | null = null;
  useRecording = false;

  result: BenchmarkRecord | null = null;
  errorMessage = '';

  history: BenchmarkRecord[] = [];
  historyTotal = 0;
  historyPage = 0;
  historyLoading = false;

  workerHealth: WorkerHealth | null = null;
  healthLoading = false;

  private mediaStream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private recordedBlob: Blob | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private startedAt = 0;

  constructor(private readonly http: HttpClient) {}

  ngOnInit(): void {
    this.checkHealth();
    this.loadHistory();
  }

  get apiBase(): string {
    return `${environment.apiHost.replace(/\/$/, '')}/api/v1/rnd/whisper-benchmark`;
  }

  get canStart(): boolean {
    return this.state !== 'recording' && this.state !== 'running';
  }

  get canStop(): boolean {
    return this.state === 'recording';
  }

  get totalHistoryPages(): number {
    return Math.ceil(this.historyTotal / 20);
  }

  get canRun(): boolean {
    return (this.state === 'recorded' || (this.state !== 'recording' && this.state !== 'running' && !!this.selectedFile))
      && !!(this.recordedBlob || this.selectedFile);
  }

  get formattedDuration(): string {
    const m = Math.floor(this.elapsedSeconds / 60).toString().padStart(2, '0');
    const s = (this.elapsedSeconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  async checkHealth(): Promise<void> {
    this.healthLoading = true;
    try {
      const res = await firstValueFrom(this.http.get<any>(`${this.apiBase}/health`));
      this.workerHealth = res?.data || res;
    } catch {
      this.workerHealth = { status: 'unreachable' };
    } finally {
      this.healthLoading = false;
    }
  }

  get activeAudioUrl(): string {
    return this.useRecording ? this.audioUrl : this.uploadedAudioUrl;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.selectedFile = input.files[0];
      this.useRecording = false;
      this.clearRecording();
      if (this.uploadedAudioUrl) URL.revokeObjectURL(this.uploadedAudioUrl);
      this.uploadedAudioUrl = URL.createObjectURL(this.selectedFile);
      this.state = 'recorded';
    }
  }

  async startRecording(): Promise<void> {
    if (!this.canStart) return;
    this.clear(false);
    this.selectedFile = null;
    this.useRecording = true;
    this.errorMessage = '';
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      this.mimeType = this.pickMimeType();
      this.recorder = new MediaRecorder(this.mediaStream, this.mimeType ? { mimeType: this.mimeType } : undefined);
      this.chunks = [];
      this.startedAt = Date.now();
      this.elapsedSeconds = 0;
      this.state = 'recording';
      this.recorder.addEventListener('dataavailable', e => { if (e.data?.size) this.chunks.push(e.data); });
      this.recorder.addEventListener('stop', () => this.finalizeRecording());
      this.recorder.start(1000);
      this.timer = setInterval(() => { this.elapsedSeconds = Math.floor((Date.now() - this.startedAt) / 1000); }, 250);
    } catch (err) {
      this.state = 'failed';
      this.errorMessage = err instanceof Error ? err.message : 'Microphone access failed.';
      this.stopTracks();
    }
  }

  stopRecording(): void {
    if (!this.recorder || this.recorder.state === 'inactive') return;
    this.recorder.stop();
  }

  async runBenchmark(): Promise<void> {
    const blob = this.recordedBlob || (this.selectedFile ? this.selectedFile : null);
    if (!blob) return;

    this.state = 'running';
    this.result = null;
    this.errorMessage = '';

    const form = new FormData();
    const filename = this.selectedFile?.name || `recorded-${Date.now()}.${this.fileExtension()}`;
    form.append('file', blob, filename);
    if (this.testName.trim()) form.append('testName', this.testName.trim());
    if (this.language.trim()) form.append('language', this.language.trim());

    try {
      const res = await firstValueFrom(this.http.post<any>(`${this.apiBase}/run`, form));
      this.result = res?.data || res;
      this.state = 'done';
      this.loadHistory();
    } catch (err: any) {
      this.state = 'failed';
      this.errorMessage = err?.error?.message || err?.message || 'Benchmark failed.';
    }
  }

  async loadHistory(page = 0): Promise<void> {
    this.historyLoading = true;
    this.historyPage = page;
    try {
      const res = await firstValueFrom(
        this.http.get<any>(`${this.apiBase}/history?page=${page}&size=20`)
      );
      const payload: HistoryPage = res?.data || res;
      this.history = payload.records || [];
      this.historyTotal = payload.totalElements || 0;
    } catch {
      this.history = [];
    } finally {
      this.historyLoading = false;
    }
  }

  async loadSegments(record: BenchmarkRecord): Promise<void> {
    if (record._expanded) { record._expanded = false; return; }
    if (!record.segments) {
      try {
        const res = await firstValueFrom(this.http.get<any>(`${this.apiBase}/${record.id}`));
        const detail = res?.data || res;
        record.segments = detail.segments || [];
        record.transcription = detail.transcription || record.transcription;
      } catch { record.segments = []; }
    }
    record._expanded = true;
  }

  async deleteRecord(record: BenchmarkRecord): Promise<void> {
    if (!confirm(`Delete benchmark #${record.id}?`)) return;
    try {
      await firstValueFrom(this.http.delete(`${this.apiBase}/${record.id}`));
      this.history = this.history.filter(r => r.id !== record.id);
      this.historyTotal--;
    } catch (err: any) {
      this.errorMessage = err?.error?.message || 'Delete failed.';
    }
  }

  clear(resetState = true): void {
    this.clearRecording();
    if (this.uploadedAudioUrl) { URL.revokeObjectURL(this.uploadedAudioUrl); this.uploadedAudioUrl = ''; }
    this.selectedFile = null;
    this.result = null;
    this.errorMessage = '';
    if (resetState) this.state = 'idle';
  }

  msBar(ms?: number, total?: number): number {
    if (!ms || !total || total === 0) return 0;
    return Math.min(Math.round((ms / total) * 100), 100);
  }

  fmt(ms?: number): string {
    if (ms === undefined || ms === null) return '-';
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(2)} s`;
  }

  fmtDuration(s?: number): string {
    if (s === undefined || s === null) return '-';
    return `${s.toFixed(2)}s`;
  }

  fmtDate(d?: string): string {
    if (!d) return '-';
    return new Date(d).toLocaleString();
  }

  fmtSize(bytes?: number): string {
    if (!bytes) return '-';
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }

  ngOnDestroy(): void {
    this.clearRecording();
  }

  private clearRecording(): void {
    if (this.recorder && this.recorder.state !== 'inactive') { try { this.recorder.stop(); } catch {} }
    this.stopTimer();
    this.stopTracks();
    this.recorder = null;
    this.chunks = [];
    this.recordedBlob = null;
    this.fileSize = 0;
    this.mimeType = '';
    this.elapsedSeconds = 0;
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    this.audioUrl = '';
  }

  private finalizeRecording(): void {
    this.stopTimer();
    this.stopTracks();
    this.recordedBlob = new Blob(this.chunks, { type: this.mimeType || 'audio/webm' });
    this.fileSize = this.recordedBlob.size;
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    this.audioUrl = URL.createObjectURL(this.recordedBlob);
    this.state = this.recordedBlob.size ? 'recorded' : 'failed';
    if (!this.recordedBlob.size) this.errorMessage = 'No audio was captured.';
  }

  private pickMimeType(): string {
    return ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg']
      .find(t => MediaRecorder.isTypeSupported(t)) || '';
  }

  private fileExtension(): string {
    return this.mimeType.includes('ogg') ? 'ogg' : 'webm';
  }

  private stopTracks(): void {
    this.mediaStream?.getTracks().forEach(t => t.stop());
    this.mediaStream = null;
  }

  private stopTimer(): void {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }
}
